/**
 * CarerProfileDetail — dedicated full-profile page linked from the
 * "Browse Carer Profiles" compact preview cards (CarerProfiles.jsx).
 *
 * Fetches the exact same family-safe projection as the browse list
 * (GET /api/carer-profile/published/:profileId — same
 * `public_profile_view()` in care_profiles.py) and renders every field
 * in full: nothing here was removed or shortened, only the browse
 * cards are compact. The "Send an enquiry" / "Invite to view my Care
 * Need" actions live here too — unchanged from before.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, ShieldCheck, ClipboardCheck, Users2, CalendarClock, Send, UserPlus,
  X, Loader2, CheckCircle2, AlertTriangle, MessageSquare, ArrowLeft, LogIn,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useHandoverSession } from "../hooks/useHandoverSession";

const CARE_TYPES = ["Live-in Care", "Dementia Care", "Mobility Support", "Personal Care", "Couples Care", "End-of-life care", "Respite care", "Hourly Care"];

function Chip({ children }) {
  return <span className="pill bg-slate-100 text-ink text-xs">{children}</span>;
}

const ENQUIRY_STATUS_NOTE = {
  submitted: { text: "Enquiry sent — awaiting response", cls: "text-amber-700" },
  interested: { text: "This carer is interested in your enquiry", cls: "text-teal" },
  more_info_requested: { text: "This carer asked for more information", cls: "text-amber-700" },
  declined: { text: "This carer declined your last enquiry — you can send another after a short wait", cls: "text-ink-muted" },
};

function EnquiryModal({ profile, onClose, onSent }) {
  const [form, setForm] = useState({ title: "", general_location: "", care_type: "", work_pattern: "", start_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.title.trim() || !form.general_location.trim() || !form.care_type || !form.work_pattern.trim() || !form.message.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/api/carer-profile/${profile.profile_id}/enquiry`, {
        ...form,
        start_date: form.start_date || null,
      });
      toast.success("Your enquiry has been sent.");
      onSent();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not send your enquiry.");
    } finally { setSubmitting(false); }
  };

  const inputCls = "mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="enquiry-modal-overlay">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="enquiry-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="overline text-purple">Send an enquiry</div>
            <h2 className="mt-1 font-display font-semibold text-lg">To {profile.name}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="enquiry-modal-close"><X className="w-5 h-5 text-ink-muted" /></button>
        </div>
        <p className="mt-3 text-xs text-ink-muted flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Please don't include detailed medical information, full addresses or other sensitive personal details at
          this stage. This is a short first approach, not a conversation thread.
        </p>
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-ink">Brief title or reason for the enquiry</span>
            <input value={form.title} onChange={set("title")} className={inputCls} data-testid="enquiry-title-input" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink">General care location</span>
            <input value={form.general_location} onChange={set("general_location")} className={inputCls} data-testid="enquiry-location-input" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink">Type of care required</span>
            <select value={form.care_type} onChange={set("care_type")} className={inputCls} data-testid="enquiry-care-type-select">
              <option value="">Select…</option>
              {CARE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink">Expected work pattern or approximate hours</span>
            <input value={form.work_pattern} onChange={set("work_pattern")} placeholder="e.g. 3 mornings a week" className={inputCls} data-testid="enquiry-work-pattern-input" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink">Proposed start date (if known)</span>
            <input type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} data-testid="enquiry-start-date-input" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink">Short introductory message</span>
            <textarea value={form.message} onChange={set("message")} rows={4} className={inputCls} data-testid="enquiry-message-input" />
          </label>
        </div>
        <button type="button" onClick={submit} disabled={submitting} className="btn-purple mt-5 w-full justify-center" data-testid="enquiry-submit-btn">
          <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send enquiry"}
        </button>
      </div>
    </div>
  );
}

function InviteModal({ profile, onClose }) {
  const [careNeeds, setCareNeeds] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/care-needs/mine");
        const posts = (data.posts || data.care_needs || []).filter((p) => p.status === "approved");
        setCareNeeds(posts);
      } catch { setCareNeeds([]); }
    })();
  }, []);

  const submit = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/api/care-needs/${selectedId}/invite-carer`, { profile_id: profile.profile_id });
      toast.success(data.already_sent ? "This carer has already been invited to this Care Need." : "Invitation sent.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not send this invitation.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="invite-modal-overlay">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="overline text-purple">Invite to view a Care Need</div>
            <h2 className="mt-1 font-display font-semibold text-lg">Invite {profile.name}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="invite-modal-close"><X className="w-5 h-5 text-ink-muted" /></button>
        </div>
        <p className="mt-3 text-sm text-ink-muted">
          Choose one of your own published Care Needs. This carer will be able to view it and respond — no contact
          details are shared, and this does not create a copy of your Care Need.
        </p>
        {careNeeds === null ? (
          <div className="mt-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-purple" /></div>
        ) : careNeeds.length === 0 ? (
          <div className="mt-6 text-center" data-testid="invite-modal-no-care-needs">
            <p className="text-sm text-ink-muted">You do not currently have a published Care Need.</p>
            <Link to="/family/care-needs" className="btn-purple mt-4 inline-flex" data-testid="invite-modal-create-care-need-link">
              Create a Care Need
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-4 space-y-2">
              {careNeeds.map((cn) => (
                <label key={cn.post_id} className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${selectedId === cn.post_id ? "border-purple bg-purple/5" : "border-slate-200"}`} data-testid={`invite-modal-care-need-${cn.post_id}`}>
                  <input type="radio" name="care_need" checked={selectedId === cn.post_id} onChange={() => setSelectedId(cn.post_id)} />
                  <span className="text-sm">
                    <span className="font-medium">{cn.care_recipient_reference || cn.care_type_label || "Care Need"}</span>
                    {cn.location_area && <span className="text-ink-muted"> • {cn.location_area}</span>}
                  </span>
                </label>
              ))}
            </div>
            <button type="button" onClick={submit} disabled={submitting || !selectedId} className="btn-purple mt-5 w-full justify-center" data-testid="invite-modal-submit-btn">
              <UserPlus className="w-4 h-4" /> {submitting ? "Sending…" : "Send invitation"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function CarerProfileDetail() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { user, checking } = useAuth();
  // Family members join/sign in via the magic-link handover session, not
  // the legacy JWT AuthContext (which is effectively admin-only these
  // days) — both must be checked to know if a "family" visitor is
  // genuinely signed in for the purposes of the contact actions below.
  const handover = useHandoverSession();
  const authReady = !checking && handover.ready;
  const isFamilyAuthed = !!user || (handover.active && (
    handover.entitlements.includes("family_free") || handover.entitlements.includes("family_circle")
  ));
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [enquiryStatus, setEnquiryStatus] = useState(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [revealing, setRevealing] = useState(false);

  // Shareable link — same path whether the visitor is logged in or not.
  const sharePath = `/carer-profiles/${profileId}`;

  // Search engines must not index individual carer profile pages while
  // publication is effectively "on by default" for every historic
  // approved profile (no informed opt-in has been collected for public,
  // internet-wide visibility yet — see PRD 2026-09-26). Applies to this
  // whole component (both the public and the gated route render it).
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const loadSentEnquiry = async () => {
    if (!isFamilyAuthed) { setEnquiryStatus(null); return; }
    try {
      const { data } = await api.get("/api/carer-profile/enquiries/sent");
      const mine = (data.enquiries || []).find((e) => e.profile_id === profileId);
      setEnquiryStatus(mine || null);
    } catch { /* non-fatal — inline status is a nice-to-have, not required for the page to work */ }
  };

  useEffect(() => {
    if (!authReady) return; // wait for both auth checks to settle before choosing an endpoint
    let alive = true;
    (async () => {
      try {
        // Logged-in family members always get the full gated projection
        // (unchanged, existing endpoint). Guests only ever see the
        // reduced, opt-in Public Sharing projection — 404s unless the
        // carer has explicitly turned Public Sharing on.
        const endpoint = isFamilyAuthed
          ? `/api/carer-profile/published/${profileId}`
          : `/api/carer-profile/public/${profileId}`;
        const { data } = await api.get(endpoint);
        if (alive) setProfile(data);
      } catch (err) {
        if (!alive) return;
        if (err?.response?.status === 404) setNotFound(true);
        else setError(true);
      }
    })();
    loadSentEnquiry();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, isFamilyAuthed, authReady]);

  const inviteToRoom = async () => {
    setRevealing(true);
    try {
      const { data } = await api.get(`/api/carer-profile/enquiries/${enquiryStatus.enquiry_id}/care-room-prefill`);
      const params = new URLSearchParams({ carer_email: data.carer_email || "", carer_name: data.carer_name || "" });
      navigate(`/care-discussion-room/new?${params.toString()}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not open the Care Room invite form.");
    } finally { setRevealing(false); }
  };

  if (!profile && !notFound && !error) {
    return (
      <div className="container-jcc py-24 text-center text-ink-muted" data-testid="carer-profile-detail-loading">
        Loading carer profile…
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="container-jcc py-24 text-center" data-testid="carer-profile-detail-not-found">
        <h1 className="font-display text-2xl font-semibold">This carer profile isn't available</h1>
        <p className="mt-2 text-ink-muted">It may have been unpublished, or the link is incorrect.</p>
        <Link to={isFamilyAuthed ? "/family/carer-profiles" : "/"} className="btn-outline mt-6 inline-flex" data-testid="carer-profile-detail-back-link">
          <ArrowLeft className="w-4 h-4" /> {isFamilyAuthed ? "Back to Browse Carer Profiles" : "Back to Just Care Community"}
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-jcc py-24 text-center" data-testid="carer-profile-detail-error">
        <p className="text-ink-muted">We couldn't load this carer profile right now. Please try again shortly.</p>
        <Link to={isFamilyAuthed ? "/family/carer-profiles" : "/"} className="btn-outline mt-6 inline-flex" data-testid="carer-profile-detail-back-link">
          <ArrowLeft className="w-4 h-4" /> {isFamilyAuthed ? "Back to Browse Carer Profiles" : "Back to Just Care Community"}
        </Link>
      </div>
    );
  }

  const p = profile;
  // Step 3 amendment (2026-08-24): every carer tier now shares the exact
  // same public profile fields (qualifications, testimonials, rates,
  // etc. are no longer Pro-only) — this key is always present now, so
  // this simply renders the "Additional details" section whenever any
  // of those optional fields have actually been filled in, for anyone.
  const hasExtendedFields = "qualifications_training" in p;
  const note = enquiryStatus ? ENQUIRY_STATUS_NOTE[enquiryStatus.status] : null;

  return (
    <div data-testid={`page-carer-profile-detail-${p.profile_id}`}>
      <section className="pt-8 pb-4">
        <div className="container-jcc max-w-3xl">
          <Link
            to={isFamilyAuthed ? "/family/carer-profiles" : "/"}
            className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink transition-colors"
            data-testid="carer-profile-detail-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> {isFamilyAuthed ? "Back to Browse Carer Profiles" : "Back to Just Care Community"}
          </Link>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-jcc max-w-3xl">
          <div className="card-base p-6 sm:p-8">
            <div className="flex items-start gap-4">
              {p.photo_data_url ? (
                <img
                  src={p.photo_data_url}
                  alt=""
                  className="w-20 h-20 rounded-full object-cover border border-slate-200 shrink-0"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-purple/10 text-purple flex items-center justify-center text-xs font-semibold shrink-0">
                  No photo
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-xl font-semibold" data-testid={`carer-profile-name-${p.profile_id}`}>
                    {p.name}
                  </div>
                </div>
                {p.general_location && (
                  <div className="mt-0.5 flex items-center gap-1.5 text-sm text-ink-muted">
                    <MapPin className="w-3.5 h-3.5 text-purple shrink-0" strokeWidth={1.75} />
                    {p.general_location}
                  </div>
                )}
              </div>
            </div>

            {(p.care_types || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {p.care_types.map((t) => <Chip key={t}>{t}</Chip>)}
              </div>
            )}

            {p.about_you && (
              <p className="mt-4 text-sm text-ink-muted leading-relaxed whitespace-pre-wrap">{p.about_you}</p>
            )}

            {p.care_experience_description && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-ink-muted mb-1">Experience</div>
                <p className="text-sm text-ink-muted leading-relaxed whitespace-pre-wrap">{p.care_experience_description}</p>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-ink-muted flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" strokeWidth={1.75} />Availability</div>
                <div className="mt-0.5" data-testid={`carer-profile-availability-${p.profile_id}`}>{p.availability_status || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-ink-muted flex items-center gap-1.5"><Users2 className="w-3.5 h-3.5" strokeWidth={1.75} />Willing to travel</div>
                <div className="mt-0.5">{p.willing_to_travel || "—"}</div>
              </div>
              {p.earliest_start_date && (
                <div>
                  <div className="text-xs text-ink-muted">Earliest start</div>
                  <div className="mt-0.5">{p.earliest_start_date}</div>
                </div>
              )}
              <div>
                <div className="text-xs text-ink-muted flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.75} />DBS status</div>
                <div className="mt-0.5">{p.dbs_status || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-ink-muted flex items-center gap-1.5"><ClipboardCheck className="w-3.5 h-3.5" strokeWidth={1.75} />Insurance</div>
                <div className="mt-0.5">{p.insurance_status || "—"}</div>
              </div>
            </div>

            {hasExtendedFields && (p.qualifications_training || p.additional_specialisms || p.professional_background
              || p.languages_spoken || p.has_driving_licence != null || p.has_vehicle != null
              || p.travel_radius_detail || p.indicative_rates || p.dbs_update_service || p.insurance_type) && (
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                <div className="text-xs font-semibold text-purple">Additional details</div>
                {p.qualifications_training && (
                  <div>
                    <div className="text-xs font-semibold text-ink-muted mb-0.5">Qualifications & training</div>
                    <p className="text-sm text-ink-muted leading-relaxed whitespace-pre-wrap">{p.qualifications_training}</p>
                  </div>
                )}
                {p.additional_specialisms && (
                  <div>
                    <div className="text-xs font-semibold text-ink-muted mb-0.5">Additional specialisms</div>
                    <p className="text-sm text-ink-muted leading-relaxed whitespace-pre-wrap">{p.additional_specialisms}</p>
                  </div>
                )}
                {p.professional_background && (
                  <div>
                    <div className="text-xs font-semibold text-ink-muted mb-0.5">Professional background</div>
                    <p className="text-sm text-ink-muted leading-relaxed whitespace-pre-wrap">{p.professional_background}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {p.languages_spoken && <Chip>Languages: {p.languages_spoken}</Chip>}
                  {p.has_driving_licence != null && <Chip>{p.has_driving_licence ? "Has driving licence" : "No driving licence"}</Chip>}
                  {p.has_vehicle != null && <Chip>{p.has_vehicle ? "Has own vehicle" : "No own vehicle"}</Chip>}
                  {p.travel_radius_detail && <Chip>{p.travel_radius_detail}</Chip>}
                  {p.indicative_rates && <Chip>Indicative rate: {p.indicative_rates}</Chip>}
                </div>
                {(p.dbs_update_service || p.insurance_type) && (
                  <div className="text-xs text-ink-muted">
                    {p.dbs_update_service && <span>DBS update service: {p.dbs_update_service}. </span>}
                    {p.insurance_type && <span>Insurance type: {p.insurance_type}.</span>}
                  </div>
                )}
              </div>
            )}

            {hasExtendedFields && p.testimonials && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-xs font-semibold text-ink-muted mb-1">Testimonials</div>
                <p className="text-sm text-ink-muted leading-relaxed whitespace-pre-wrap">{p.testimonials}</p>
              </div>
            )}

            <div className="mt-5 pt-4 border-t border-slate-100">
              {note && (
                <p className={`text-xs font-medium mb-2.5 flex items-center gap-1.5 ${note.cls}`} data-testid={`carer-profile-enquiry-status-${p.profile_id}`}>
                  {enquiryStatus.status === "interested" ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
                  {note.text}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {!isFamilyAuthed ? (
                  <div className="w-full rounded-xl bg-purple/5 border border-purple/20 p-4" data-testid={`carer-profile-guest-cta-${p.profile_id}`}>
                    <p className="text-sm text-ink">Log in or join free as a family member to contact this carer.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link
                        to={`/auth/handover/request?next=${encodeURIComponent(sharePath)}`}
                        className="btn-purple text-sm"
                        data-testid="carer-profile-guest-login-link"
                      >
                        <LogIn className="w-4 h-4" /> Log in
                      </Link>
                      <Link
                        to={`/join/family-starter?next=${encodeURIComponent(sharePath)}`}
                        className="btn-outline text-sm"
                        data-testid="carer-profile-guest-join-link"
                      >
                        Join free
                      </Link>
                    </div>
                  </div>
                ) : (
                  <>
                    {(!enquiryStatus || enquiryStatus.status === "declined") && (
                      <button
                        type="button"
                        onClick={() => setEnquiryOpen(true)}
                        className="btn-purple text-sm"
                        data-testid={`carer-profile-enquiry-btn-${p.profile_id}`}
                      >
                        <Send className="w-4 h-4" /> Send this carer an enquiry
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setInviteOpen(true)}
                      className="btn-outline text-sm"
                      data-testid={`carer-profile-invite-btn-${p.profile_id}`}
                    >
                      <UserPlus className="w-4 h-4" /> Invite to view my Care Need
                    </button>
                    {enquiryStatus?.status === "interested" && (
                      <button
                        type="button"
                        onClick={inviteToRoom}
                        disabled={revealing}
                        className="btn-teal text-sm"
                        data-testid={`carer-profile-invite-room-btn-${p.profile_id}`}
                      >
                        <MessageSquare className="w-4 h-4" /> {revealing ? "Opening…" : "Invite to a Care Room"}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <p className="mt-4 text-xs text-ink-soft">
              DBS, insurance, qualifications and testimonials shown here are declared by the carer and have not been
              independently verified by JCC unless stated otherwise.
            </p>
          </div>
        </div>
      </section>

      {enquiryOpen && (
        <EnquiryModal profile={p} onClose={() => setEnquiryOpen(false)} onSent={loadSentEnquiry} />
      )}
      {inviteOpen && (
        <InviteModal profile={p} onClose={() => setInviteOpen(false)} />
      )}
    </div>
  );
}
